/**
 * The customer's side of token expiry (BOAT-1898).
 *
 * Sessions used to last forever, so the storefront never had to have an opinion
 * about them ending. Now they end, and the only version of that worth shipping
 * is one a customer never notices: the window slides on every request, so
 * somebody who is browsing keeps their session simply by browsing, and this
 * exists for the two cases where that is not enough.
 *
 * The first is sitting still. A checkout is the longest a customer ever goes
 * without making a request — reading a card number off a bank app, being
 * interrupted — and it is the worst moment to be signed out. So a session
 * approaching its end is refreshed while the tab is open, which on the server
 * is an ordinary authenticated request and slides the window like any other.
 *
 * The second is the absolute ceiling, which no amount of refreshing moves. That
 * one really does end the session, and what matters then is what does NOT
 * happen: no page reload, no redirect, no cleared basket, no lost form. The
 * basket lives in its own cookie and the checkout takes a guest just as
 * happily, so the customer is offered the login box and carries on from exactly
 * where they were standing.
 *
 * Nothing here is derived from the token. The API says when the session ends,
 * on the login response and then on a header on every authenticated response
 * after it, because the server is the only thing that knows — it is the one
 * moving the window.
 */

export const SESSION_EXPIRY_HEADER = 'x-auth-expires-at'

/** Refresh a session with less than this left, rather than watch it die. */
export const SESSION_KEEP_ALIVE_WITHIN_MS = 30 * 60 * 1000

/** Below this, and refreshing has not helped, the customer is told. */
export const SESSION_WARN_WITHIN_MS = 5 * 60 * 1000

export const useSessionExpiry = () => {
  const nuxtApp = useNuxtApp()
  const authToken = useCookie<string | null>('authToken')
  const expiresAt = useCookie<string | null>('authExpiresAt')

  // Not a cookie: a warning is about this tab, and one shown on a laptop should
  // not be counted as shown on the phone.
  const warned = useState<boolean>('session-expiry-warned', () => false)

  const parse = (value?: string | null) => {
    if (!value) return null

    const at = new Date(value)

    return Number.isNaN(at.getTime()) ? null : at
  }

  /** How long the session has left, or null when nothing has said. */
  const msRemaining = () => {
    const at = parse(expiresAt.value)

    return at === null ? null : at.getTime() - Date.now()
  }

  const remember = (value?: string | null) => {
    const at = parse(value)

    if (at === null) return

    expiresAt.value = at.toISOString()

    // A window that has moved is a window worth warning about again.
    warned.value = false
  }

  const rememberFromHeaders = (headers: Headers) =>
    remember(headers.get(SESSION_EXPIRY_HEADER))

  const forget = () => {
    expiresAt.value = null
    warned.value = false
  }

  /**
   * The session has ended, and the customer is mid-something.
   *
   * Deliberately narrow: the token and the user go, and nothing else does. The
   * basket cookie is untouched, the route is not changed and the page is not
   * reloaded — a reload here is what would lose a half-filled checkout, which
   * is the entire failure this story set out to avoid. Signing back in through
   * the modal leaves them on the same page with the same form.
   *
   * Idempotent, because a page that fires three requests at once gets three
   * 401s and should produce one message.
   */
  const expired = () => {
    if (!authToken.value) return

    authToken.value = null
    forget()

    useAuthStore().user = null

    if (!import.meta.client) return

    nuxtApp.$toast.info(nuxtApp.$i18n.t('feedback.session.expired'))
    useModalsStore().openLoginModal()
  }

  /**
   * Says whether the session is close enough to the end to be worth refreshing.
   * Separate from doing it: the refresh is an API call, and this composable is
   * imported by the module that makes API calls.
   */
  const needsKeepAlive = () => {
    if (!authToken.value) return false

    const remaining = msRemaining()

    return remaining !== null && remaining > 0 && remaining <= SESSION_KEEP_ALIVE_WITHIN_MS
  }

  const isExpired = () => {
    const remaining = msRemaining()

    return !!authToken.value && remaining !== null && remaining <= 0
  }

  /**
   * Told once, and only when a refresh has already failed to buy more time —
   * which in practice means the account has hit the ceiling and really will
   * have to sign in again. Warning on the idle window instead would fire at
   * people whose next click was about to renew it anyway.
   */
  const warnIfEnding = () => {
    if (!import.meta.client || warned.value || !authToken.value) return

    const remaining = msRemaining()

    if (remaining === null || remaining <= 0 || remaining > SESSION_WARN_WITHIN_MS) return

    warned.value = true

    nuxtApp.$toast.warning(nuxtApp.$i18n.t('feedback.session.expiring'))
  }

  return {
    expiresAt,
    msRemaining,
    remember,
    rememberFromHeaders,
    forget,
    expired,
    isExpired,
    needsKeepAlive,
    warnIfEnding,
  }
}
